import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Line,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
} from 'recharts';
import {
  Activity,
  Users,
  MessageSquare,
  Clock,
  AlertTriangle,
  RefreshCw,
  TrendingUp,
  Download,
  Database,
  CheckCircle,
  Search,
  Filter,
  Settings,
  BarChart3,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Maximize2,
  Minimize2,
  RotateCcw,
  Share2,
  FileText,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Info,
  Star,
  Tag,
  Wifi,
  WifiOff,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
} from 'lucide-react';
import './App.css';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === 'development'
    ? 'http://localhost:3002'
    : 'https://dashboard.vortexsoftware.com.ar');

const COLORS = [
  '#667eea',
  '#764ba2',
  '#48bb78',
  '#ed8936',
  '#9f7aea',
  '#38b2ac',
  '#4299e1',
  '#d69e2e',
];

// Estados de conexión
const CONNECTION_STATUS = {
  ONLINE: 'online',
  OFFLINE: 'offline',
  CONNECTING: 'connecting',
};

// Configuración de temas
const THEMES = {
  light: {
    name: 'Claro',
    primary: '#667eea',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    cardBg: 'rgba(255, 255, 255, 0.95)',
    textPrimary: '#2d3748',
    textSecondary: '#718096',
  },
  dark: {
    name: 'Oscuro',
    primary: '#9f7aea',
    background: 'linear-gradient(135deg, #1a202c 0%, #2d3748 100%)',
    cardBg: 'rgba(45, 55, 72, 0.95)',
    textPrimary: '#f7fafc',
    textSecondary: '#a0aec0',
  },
  ocean: {
    name: 'Océano',
    primary: '#38b2ac',
    background: 'linear-gradient(135deg, #4fd1c7 0%, #3182ce 100%)',
    cardBg: 'rgba(255, 255, 255, 0.95)',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
  },
};

// Componente de métrica mejorado con animaciones y más información
const MetricCard = ({
  title,
  value,
  icon: Icon,
  color = 'blue',
  trend,
  subtitle,
  onClick,
  className = '',
}) => (
  <div
    className={`metric-card metric-card--${color} ${className} ${
      onClick ? 'metric-card--clickable' : ''
    }`}
    onClick={onClick}
  >
    <div className="metric-card__header">
      <div className="metric-card__icon">
        <Icon size={20} />
      </div>
      <div className="metric-card__info">
        <h3>{title}</h3>
        {subtitle && <span className="metric-card__subtitle">{subtitle}</span>}
      </div>
      {trend && (
        <div
          className={`metric-card__trend metric-card__trend--${trend.direction}`}
        >
          {trend.direction === 'up' ? (
            <ArrowUpRight size={16} />
          ) : (
            <ArrowDownRight size={16} />
          )}
          <span>{trend.value}</span>
        </div>
      )}
    </div>
    <p className="metric-card__value">{value}</p>
  </div>
);

// Componente de conexión en tiempo real
const ConnectionStatus = ({ status }) => (
  <div className={`connection-status connection-status--${status}`}>
    {status === CONNECTION_STATUS.ONLINE && <Wifi size={16} />}
    {status === CONNECTION_STATUS.OFFLINE && <WifiOff size={16} />}
    {status === CONNECTION_STATUS.CONNECTING && (
      <RefreshCw className="spin" size={16} />
    )}
    <span>
      {status === CONNECTION_STATUS.ONLINE && 'Conectado'}
      {status === CONNECTION_STATUS.OFFLINE && 'Desconectado'}
      {status === CONNECTION_STATUS.CONNECTING && 'Conectando...'}
    </span>
  </div>
);

// Componente de filtros avanzados
const FilterPanel = ({
  filters,
  onFilterChange,
  onReset,
  isOpen,
  onToggle,
}) => (
  <div className={`filter-panel ${isOpen ? 'filter-panel--open' : ''}`}>
    <div className="filter-panel__header" onClick={onToggle}>
      <div className="filter-panel__title">
        <Filter size={18} />
        <span>Filtros Avanzados</span>
      </div>
      {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
    </div>
    {isOpen && (
      <div className="filter-panel__content">
        <div className="filter-group">
          <label>Rango de fechas:</label>
          <div className="filter-date-range">
            <input
              type="date"
              value={filters.dateFrom || ''}
              onChange={(e) => onFilterChange('dateFrom', e.target.value)}
            />
            <span>hasta</span>
            <input
              type="date"
              value={filters.dateTo || ''}
              onChange={(e) => onFilterChange('dateTo', e.target.value)}
            />
          </div>
        </div>
        <div className="filter-group">
          <label>Tipo de análisis:</label>
          <select
            value={filters.analysisType || 'all'}
            onChange={(e) => onFilterChange('analysisType', e.target.value)}
          >
            <option value="all">Todos</option>
            <option value="completed">Solo completadas</option>
            <option value="abandoned">Solo abandonadas</option>
            <option value="escalated">Solo escaladas</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Duración mínima (min):</label>
          <input
            type="number"
            min="0"
            value={filters.minDuration || ''}
            onChange={(e) => onFilterChange('minDuration', e.target.value)}
            placeholder="0"
          />
        </div>
        <div className="filter-actions">
          <button className="btn btn--secondary btn--small" onClick={onReset}>
            <RotateCcw size={14} />
            Resetear
          </button>
        </div>
      </div>
    )}
  </div>
);

// Componente de tarjeta de proyecto mejorado
const ProjectCard = ({
  project,
  onAnalyze,
  isLoading,
  onFavorite,
  isFavorite,
}) => (
  <div className="project-card">
    <div className="project-card__header">
      <div className="project-card__title">
        <h3>{project.name}</h3>
        <span className="project-id">#{project.id}</span>
      </div>
      <div className="project-card__actions">
        <button
          className={`btn-icon ${isFavorite ? 'btn-icon--active' : ''}`}
          onClick={() => onFavorite(project.id)}
          title={isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
        >
          {isFavorite ? (
            <Star size={16} fill="currentColor" />
          ) : (
            <Star size={16} />
          )}
        </button>
      </div>
    </div>
    <p className="project-description">{project.description}</p>
    <div className="project-card__meta">
      <span className="project-meta__item">
        <Calendar size={14} />
        Última actualización:{' '}
        {new Date(project.lastUpdated).toLocaleDateString()}
      </span>
    </div>
    <div className="project-card__footer">
      <button
        className={`btn btn--primary ${isLoading ? 'btn--loading' : ''}`}
        onClick={() => onAnalyze(project.id)}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <RefreshCw className="spin" size={16} />
            Analizando...
          </>
        ) : (
          <>
            <Activity size={16} />
            Analizar
          </>
        )}
      </button>
    </div>
  </div>
);

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <RefreshCw className="spin" size={24} />
    <span>Analizando datos...</span>
  </div>
);

// Componente de visualización de gráficos mejorado
const ChartContainer = ({
  title,
  subtitle,
  children,
  onExpand,
  isExpanded,
  onRefresh,
  options,
}) => (
  <div
    className={`chart-container ${
      isExpanded ? 'chart-container--expanded' : ''
    }`}
  >
    <div className="chart-container__header">
      <div className="chart-container__title">
        <h3>{title}</h3>
        {subtitle && (
          <span className="chart-container__subtitle">{subtitle}</span>
        )}
      </div>
      <div className="chart-container__actions">
        {onRefresh && (
          <button className="btn-icon" onClick={onRefresh} title="Actualizar">
            <RefreshCw size={16} />
          </button>
        )}
        {onExpand && (
          <button
            className="btn-icon"
            onClick={onExpand}
            title={isExpanded ? 'Minimizar' : 'Expandir'}
          >
            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        )}
        <button className="btn-icon" title="Compartir">
          <Share2 size={16} />
        </button>
      </div>
    </div>
    <div className="chart-container__content">{children}</div>
    {options && <div className="chart-container__options">{options}</div>}
  </div>
);

// Componente de búsqueda avanzada
const SearchBar = ({
  value,
  onChange,
  placeholder,
  filters,
  onFilterSelect,
}) => (
  <div className="search-bar">
    <div className="search-bar__input">
      <Search size={18} />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Buscar en análisis...'}
      />
    </div>
    {filters && filters.length > 0 && (
      <div className="search-bar__filters">
        {filters.map((filter, index) => (
          <button
            key={index}
            className="search-filter"
            onClick={() => onFilterSelect(filter)}
          >
            <Tag size={14} />
            {filter.label}
          </button>
        ))}
      </div>
    )}
  </div>
);

// Componente de notificaciones/alertas
const NotificationBanner = ({ type, message, onDismiss, action }) => (
  <div className={`notification-banner notification-banner--${type}`}>
    <div className="notification-banner__content">
      <div className="notification-banner__icon">
        {type === 'success' && <CheckCircle size={20} />}
        {type === 'warning' && <AlertTriangle size={20} />}
        {type === 'error' && <AlertTriangle size={20} />}
        {type === 'info' && <Info size={20} />}
      </div>
      <span>{message}</span>
    </div>
    <div className="notification-banner__actions">
      {action && (
        <button className="btn btn--small btn--ghost" onClick={action.onClick}>
          {action.label}
        </button>
      )}
      <button className="btn-icon btn-icon--ghost" onClick={onDismiss}>
        ×
      </button>
    </div>
  </div>
);

const ProgressLoader = ({
  stage,
  progress,
  message,
  processed,
  total,
  page,
  conversationsInPage,
  totalConversations,
}) => {
  const getStageIcon = () => {
    switch (stage) {
      case 'fetching':
        return <Download size={20} />;
      case 'processing':
        return <Database size={20} />;
      default:
        return <RefreshCw size={20} />;
    }
  };

  const getStageTitle = () => {
    switch (stage) {
      case 'fetching':
        return 'Obteniendo Conversaciones';
      case 'processing':
        return 'Procesando Datos';
      default:
        return 'Analizando';
    }
  };

  return (
    <div className="progress-loader" data-stage={stage}>
      <div className="progress-header">
        {getStageIcon()}
        <h3>{getStageTitle()}</h3>
      </div>

      <div className="progress-bar-container">
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <span className="progress-text">{progress}%</span>
      </div>

      <p className="progress-message">{message}</p>

      {processed && total && (
        <div className="progress-details">
          <span>
            {processed} de {total} conversaciones
          </span>
        </div>
      )}

      {page && conversationsInPage && (
        <div className="progress-details">
          <span>
            Página {page}: {conversationsInPage} conversaciones obtenidas
          </span>
        </div>
      )}

      {totalConversations && stage === 'fetching' && (
        <div className="progress-details">
          <span>Total acumulado: {totalConversations} conversaciones</span>
        </div>
      )}
    </div>
  );
};

// Componente original de ProjectCard ahora está arriba con mejoras

const CacheManager = ({
  cacheInfo,
  projectsCacheInfo,
  projects,
  onClearAll,
  onClearExpired,
  onDeleteSpecific,
  onRefresh,
  loading = false,
}) => {
  const [expandedSections, setExpandedSections] = useState(new Set());

  const toggleSection = (projectId) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatExpirationDate = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProjectName = (projectId) => {
    const project = projects.find(
      (p) =>
        p.name
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '') === projectId
    );
    return project ? project.name : projectId;
  };

  return (
    <div className="cache-manager">
      <div className="cache-manager__header">
        <h2>📊 Gestor de Caché por Proyectos</h2>
        <div className="cache-manager__stats">
          <div className="stat-card">
            <span className="stat-label">Total Proyectos</span>
            <span className="stat-value">{projectsCacheInfo.length}</span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Archivos de Caché</span>
            <span className="stat-value">
              {projectsCacheInfo.reduce(
                (total, project) => total + project.totalFiles,
                0
              )}
            </span>
          </div>
          <div className="stat-card">
            <span className="stat-label">Tamaño Total</span>
            <span className="stat-value">
              {formatFileSize(
                projectsCacheInfo.reduce(
                  (total, project) => total + project.totalSize,
                  0
                )
              )}
            </span>
          </div>
        </div>
        <div className="cache-manager__actions">
          <button className="btn btn--secondary" onClick={onRefresh}>
            🔄 Actualizar
          </button>
          <button className="btn btn--warning" onClick={onClearExpired}>
            🧹 Limpiar Expirados
          </button>
          <button className="btn btn--danger" onClick={onClearAll}>
            🗑️ Limpiar Todo
          </button>
        </div>
      </div>

      {loading ? (
        <div className="cache-manager__loading">
          <RefreshCw className="spin" size={24} />
          <span>Cargando información de caché...</span>
        </div>
      ) : projectsCacheInfo && projectsCacheInfo.length > 0 ? (
        <div className="projects-cache-grid">
          {projectsCacheInfo.map((projectCache, index) => {
            const isExpanded = expandedSections.has(projectCache.projectId);
            return (
              <div key={index} className="project-cache-card">
                <div
                  className="project-cache-header"
                  onClick={() => toggleSection(projectCache.projectId)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="project-cache-header-content">
                    <h3>{getProjectName(projectCache.projectId)}</h3>
                    <div className="project-cache-stats">
                      <span className="cache-count">
                        {projectCache.totalFiles} archivos
                      </span>
                      <span className="cache-size">
                        {formatFileSize(projectCache.totalSize)}
                      </span>
                    </div>
                  </div>
                  <div className="project-cache-toggle">
                    <span
                      className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}
                    >
                      {isExpanded ? '▼' : '▶'}
                    </span>
                  </div>
                </div>

                {isExpanded && (
                  <div className="project-cache-content">
                    {projectCache.files && projectCache.files.length > 0 ? (
                      <div className="project-cache-files">
                        {projectCache.files.map((file, fileIndex) => (
                          <div
                            key={fileIndex}
                            className={`project-cache-file ${
                              file.isExpired
                                ? 'cache-file--expired'
                                : 'cache-file--valid'
                            }`}
                          >
                            <div className="cache-file__info">
                              <div className="cache-file__name">
                                {file.name.includes('analysis_')
                                  ? '📊 Análisis completo'
                                  : '💾 Conversaciones'}
                              </div>
                              <div className="cache-file__details">
                                <span className="cache-file__size">
                                  {formatFileSize(file.size)}
                                </span>
                                <span className="cache-file__expires">
                                  Vence: {formatExpirationDate(file.expiresAt)}
                                </span>
                                <span className="cache-file__expires-in">
                                  {file.expiresIn}
                                </span>
                                <span
                                  className={`cache-file__status ${
                                    file.isExpired
                                      ? 'status--expired'
                                      : 'status--valid'
                                  }`}
                                >
                                  {file.isExpired ? '❌ Expirado' : '✅ Válido'}
                                </span>
                              </div>
                            </div>
                            <div className="cache-file__actions">
                              <button
                                className="btn btn--danger btn--small"
                                onClick={() =>
                                  onDeleteSpecific(
                                    file.name.replace('.json', '')
                                  )
                                }
                                title="Eliminar este caché"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="project-cache-empty">
                        <p>📭 Sin caché</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="cache-manager__empty">
          <p>📭 No hay cachés de proyectos</p>
        </div>
      )}
    </div>
  );
};

const ProjectManager = ({
  projects,
  onAddProject,
  onEditProject,
  onDeleteProject,
  onRefresh,
  loading = false,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    apiUrl: '',
    accountId: '',
    token: '',
    operatorPhrase: '',
    infoLinks: '',
    humanRequestKeyword: '',
    botName: '',
    enableCache: true,
    cacheDuration: 24 * 60 * 60 * 1000,
  });

  const handleOpenModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        name: project.name || '',
        description: project.description || '',
        apiUrl: project.config?.apiUrl || '',
        accountId: project.config?.accountId || '',
        token: project.config?.token || '',
        operatorPhrase: project.config?.operatorPhrase || '',
        infoLinks: Array.isArray(project.config?.infoLinks)
          ? project.config.infoLinks.join('\n')
          : '',
        humanRequestKeyword: project.config?.humanRequestKeyword || '',
        botName: project.config?.botName || '',
        enableCache: project.config?.enableCache !== false,
        cacheDuration: project.config?.cacheDuration || 24 * 60 * 60 * 1000,
      });
    } else {
      setEditingProject(null);
      setFormData({
        name: '',
        description: '',
        apiUrl: '',
        accountId: '',
        token: '',
        operatorPhrase: '',
        infoLinks: '',
        humanRequestKeyword: '',
        botName: '',
        enableCache: true,
        cacheDuration: 24 * 60 * 60 * 1000,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const projectData = {
      ...formData,
      infoLinks: formData.infoLinks.split('\n').filter((link) => link.trim()),
      cacheDuration: parseInt(formData.cacheDuration),
    };

    if (editingProject) {
      await onEditProject(editingProject.id, projectData);
    } else {
      await onAddProject(projectData);
    }

    setIsModalOpen(false);
  };

  const handleDelete = async (projectId) => {
    if (
      window.confirm('¿Estás seguro de que quieres eliminar este proyecto?')
    ) {
      await onDeleteProject(projectId);
    }
  };

  return (
    <div className="project-manager">
      <div className="project-manager__header">
        <h3>📁 Gestión de Proyectos</h3>
        <div className="project-manager__actions">
          <button
            className="btn btn--primary"
            onClick={() => handleOpenModal()}
            disabled={loading}
          >
            <Plus size={16} />
            Nuevo Proyecto
          </button>
          <button
            className="btn btn--secondary"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw size={16} />
            Actualizar
          </button>
        </div>
      </div>

      <div className="project-manager__list">
        {projects.length > 0 ? (
          projects.map((project) => (
            <div key={project.id} className="project-manager__item">
              <div className="project-manager__item-info">
                <h4>{project.name}</h4>
                <p>{project.description}</p>
                <small>ID: {project.id}</small>
              </div>
              <div className="project-manager__item-actions">
                <button
                  className="btn btn--icon"
                  onClick={() => handleOpenModal(project)}
                  title="Editar proyecto"
                >
                  <Edit size={16} />
                </button>
                <button
                  className="btn btn--icon btn--danger"
                  onClick={() => handleDelete(project.id)}
                  title="Eliminar proyecto"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="project-manager__empty">
            <p>📭 No hay proyectos configurados</p>
          </div>
        )}
      </div>

      {/* Modal para agregar/editar proyecto */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal__header">
              <h3>{editingProject ? 'Editar Proyecto' : 'Nuevo Proyecto'}</h3>
              <button
                className="btn btn--icon"
                onClick={() => setIsModalOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal__form">
              <div className="form-group">
                <label>Nombre del Proyecto *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>URL de la API *</label>
                <input
                  type="url"
                  value={formData.apiUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, apiUrl: e.target.value })
                  }
                  placeholder="https://chatwoot.tucliente.com/api/v1"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ID de Cuenta *</label>
                  <input
                    type="text"
                    value={formData.accountId}
                    onChange={(e) =>
                      setFormData({ ...formData, accountId: e.target.value })
                    }
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Token de API *</label>
                  <input
                    type="password"
                    value={formData.token}
                    onChange={(e) =>
                      setFormData({ ...formData, token: e.target.value })
                    }
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Frase para Operador</label>
                <input
                  type="text"
                  value={formData.operatorPhrase}
                  onChange={(e) =>
                    setFormData({ ...formData, operatorPhrase: e.target.value })
                  }
                  placeholder="quiero hablar con un operador"
                />
              </div>

              <div className="form-group">
                <label>Links de Información (uno por línea)</label>
                <textarea
                  value={formData.infoLinks}
                  onChange={(e) =>
                    setFormData({ ...formData, infoLinks: e.target.value })
                  }
                  placeholder="https://midominio.com/info&#10;https://otrodominio.com/ayuda"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Palabra Clave para Humano</label>
                  <input
                    type="text"
                    value={formData.humanRequestKeyword}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        humanRequestKeyword: e.target.value,
                      })
                    }
                    placeholder="asesor"
                  />
                </div>
                <div className="form-group">
                  <label>Nombre del Bot</label>
                  <input
                    type="text"
                    value={formData.botName}
                    onChange={(e) =>
                      setFormData({ ...formData, botName: e.target.value })
                    }
                    placeholder="Nombre del Bot"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.enableCache}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          enableCache: e.target.checked,
                        })
                      }
                    />
                    Habilitar Caché
                  </label>
                </div>
                <div className="form-group">
                  <label>Duración del Caché (ms)</label>
                  <input
                    type="number"
                    value={formData.cacheDuration}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        cacheDuration: e.target.value,
                      })
                    }
                    min="0"
                  />
                </div>
              </div>

              <div className="modal__actions">
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={loading}
                >
                  <Save size={16} />
                  {editingProject ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

function DashboardApp() {
  // Helper para hacer fetch con autenticación
  const fetchWithAuth = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }, []);

  // Estados existentes
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analyzingProject, setAnalyzingProject] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [projectsCacheInfo, setProjectsCacheInfo] = useState([]);
  const [showCacheManager, setShowCacheManager] = useState(false);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [timeFilter, setTimeFilter] = useState({ min: 1, max: 25 });
  const [slowConversationsPage, setSlowConversationsPage] = useState(1);

  // Nuevos estados para funcionalidades mejoradas
  const [theme, setTheme] = useState('light');
  const [connectionStatus, setConnectionStatus] = useState(
    CONNECTION_STATUS.ONLINE
  );
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    analysisType: 'all',
    minDuration: '',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [favoriteProjects, setFavoriteProjects] = useState(new Set());
  const [expandedCharts, setExpandedCharts] = useState(new Set());
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [showProjectManager, setShowProjectManager] = useState(false);
  const [projectManagerLoading, setProjectManagerLoading] = useState(false);

  // Efectos para cargar datos iniciales y configurar funcionalidades
  useEffect(() => {
    const initApp = async () => {
      await fetchProjects();
      await fetchCacheInfo();
      await fetchProjectsCacheInfo();
      loadUserPreferences();
      setupConnectionMonitoring();
    };
    initApp();
  }, []);

  // Efecto para auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchCacheInfo();
        fetchProjectsCacheInfo();
      }, 30000); // 30 segundos
      setRefreshInterval(interval);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }

    return () => {
      if (refreshInterval) clearInterval(refreshInterval);
    };
  }, [autoRefresh]);

  // Efecto para actualizar caché cuando se muestra el gestor
  useEffect(() => {
    if (showCacheManager) {
      fetchCacheInfo();
      fetchProjectsCacheInfo();
    }
  }, [showCacheManager]);

  // Función para cargar preferencias del usuario
  const loadUserPreferences = useCallback(() => {
    try {
      const savedTheme = localStorage.getItem('dashboard-theme') || 'light';
      const savedFavorites = JSON.parse(
        localStorage.getItem('dashboard-favorites') || '[]'
      );
      const savedViewMode =
        localStorage.getItem('dashboard-view-mode') || 'grid';

      setTheme(savedTheme);
      setFavoriteProjects(new Set(savedFavorites));
      setViewMode(savedViewMode);
    } catch (error) {
      console.warn('Error cargando preferencias:', error);
    }
  }, []);

  // Función para monitorear conexión
  const setupConnectionMonitoring = useCallback(() => {
    const checkConnection = async () => {
      try {
        setConnectionStatus(CONNECTION_STATUS.CONNECTING);
        const response = await fetchWithAuth(`${API_BASE}/api/projects`);
        if (response.ok) {
          setConnectionStatus(CONNECTION_STATUS.ONLINE);
        } else {
          setConnectionStatus(CONNECTION_STATUS.OFFLINE);
        }
      } catch (error) {
        setConnectionStatus(CONNECTION_STATUS.OFFLINE);
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);
  }, [fetchWithAuth]);

  // Funciones de gestión de notificaciones
  const addNotification = useCallback((type, message, action = null) => {
    const id = Date.now().toString();
    const notification = { id, type, message, action };
    setNotifications((prev) => [...prev, notification]);

    // Auto-remove después de 5 segundos para success/info
    if (type === 'success' || type === 'info') {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, 5000);
    }
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Funciones de gestión de favoritos
  const toggleFavorite = useCallback((projectId) => {
    setFavoriteProjects((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(projectId)) {
        newFavorites.delete(projectId);
      } else {
        newFavorites.add(projectId);
      }
      localStorage.setItem(
        'dashboard-favorites',
        JSON.stringify([...newFavorites])
      );
      return newFavorites;
    });
  }, []);

  // Funciones de gestión de filtros
  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      analysisType: 'all',
      minDuration: '',
    });
  }, []);

  // Funciones de gestión de tema
  const changeTheme = useCallback((newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('dashboard-theme', newTheme);
    document.body.style.background = THEMES[newTheme].background;
  }, []);

  // Funciones de gestión de gráficos
  const toggleChartExpansion = useCallback((chartId) => {
    setExpandedCharts((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(chartId)) {
        newExpanded.delete(chartId);
      } else {
        newExpanded.add(chartId);
      }
      return newExpanded;
    });
  }, []);

  // Proyectos filtrados y con búsqueda
  const filteredProjects = useMemo(() => {
    let filtered = projects;

    // Filtro de búsqueda
    if (searchQuery) {
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          project.description
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          project.id.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Ordenar favoritos primero
    filtered.sort((a, b) => {
      const aFav = favoriteProjects.has(a.id);
      const bFav = favoriteProjects.has(b.id);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return 0;
    });

    return filtered;
  }, [projects, searchQuery, favoriteProjects]);

  const fetchCacheInfo = async () => {
    try {
      setCacheLoading(true);
      const response = await fetchWithAuth(`${API_BASE}/api/cache/info`);
      const data = await response.json();
      if (data.success) {
        setCacheInfo(data.data);
      }
    } catch (err) {
      console.error('Error obteniendo información de caché:', err);
      addNotification('error', 'Error al cargar información de caché');
    } finally {
      setCacheLoading(false);
    }
  };

  const fetchProjectsCacheInfo = async () => {
    try {
      setCacheLoading(true);
      const response = await fetchWithAuth(`${API_BASE}/api/cache/projects`);
      const data = await response.json();
      if (data.success) {
        setProjectsCacheInfo(data.data);
      }
    } catch (err) {
      console.error(
        'Error obteniendo información de caché por proyectos:',
        err
      );
      addNotification(
        'error',
        'Error al cargar información de caché por proyectos'
      );
    } finally {
      setCacheLoading(false);
    }
  };

  const clearAllCache = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/cache/clear`, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (data.success) {
        addNotification('success', data.message);
        await fetchCacheInfo();
        await fetchProjectsCacheInfo();
      } else {
        addNotification('error', 'Error: ' + data.error);
      }
    } catch (err) {
      addNotification('error', 'Error al limpiar caché: ' + err.message);
    }
  };

  const clearExpiredCache = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/cache/clear-expired`,
        {
          method: 'DELETE',
        }
      );
      const data = await response.json();
      if (data.success) {
        addNotification('success', data.message);
        await fetchCacheInfo();
        await fetchProjectsCacheInfo();
      } else {
        addNotification('error', 'Error: ' + data.error);
      }
    } catch (err) {
      addNotification(
        'error',
        'Error al limpiar caché expirado: ' + err.message
      );
    }
  };

  const deleteSpecificCache = async (cacheKey) => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/api/cache/${cacheKey}`,
        {
          method: 'DELETE',
        }
      );
      const data = await response.json();
      if (data.success) {
        addNotification('success', data.message);
        await fetchCacheInfo();
        await fetchProjectsCacheInfo();
      } else {
        addNotification('error', 'Error: ' + data.error);
      }
    } catch (err) {
      addNotification('error', 'Error al eliminar caché: ' + err.message);
    }
  };

  // Funciones para gestión de proyectos
  const addProject = async (projectData) => {
    try {
      setProjectManagerLoading(true);
      const response = await fetchWithAuth(`${API_BASE}/api/projects`, {
        method: 'POST',
        body: JSON.stringify(projectData),
      });

      if (response.ok) {
        await fetchProjects();
        addNotification('success', 'Proyecto creado correctamente');
      } else {
        const error = await response.json();
        addNotification('error', `Error creando proyecto: ${error.error}`);
      }
    } catch (error) {
      addNotification('error', `Error de conexión: ${error.message}`);
    } finally {
      setProjectManagerLoading(false);
    }
  };

  const editProject = async (projectId, projectData) => {
    try {
      setProjectManagerLoading(true);
      const response = await fetchWithAuth(
        `${API_BASE}/api/projects/${projectId}`,
        {
          method: 'PUT',
          body: JSON.stringify(projectData),
        }
      );

      if (response.ok) {
        await fetchProjects();
        addNotification('success', 'Proyecto actualizado correctamente');
      } else {
        const error = await response.json();
        addNotification('error', `Error actualizando proyecto: ${error.error}`);
      }
    } catch (error) {
      addNotification('error', `Error de conexión: ${error.message}`);
    } finally {
      setProjectManagerLoading(false);
    }
  };

  const deleteProject = async (projectId) => {
    try {
      setProjectManagerLoading(true);
      const response = await fetchWithAuth(
        `${API_BASE}/api/projects/${projectId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.ok) {
        await fetchProjects();
        addNotification('success', 'Proyecto eliminado correctamente');
      } else {
        const error = await response.json();
        addNotification('error', `Error eliminando proyecto: ${error.error}`);
      }
    } catch (error) {
      addNotification('error', `Error de conexión: ${error.message}`);
    } finally {
      setProjectManagerLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/projects`);
      const data = await response.json();
      if (data.success) {
        setProjects(data.data);
      } else {
        setError('Error al cargar proyectos');
      }
    } catch (err) {
      setError('Error de conexión al servidor');
      console.error('Error:', err);
    }
  };

  const analyzeProject = async (projectId) => {
    setAnalyzingProject(projectId);
    setLoading(true);
    setError(null);
    setProgressData(null);

    // Agregar notificación de inicio
    const projectName =
      projects.find((p) => p.id === projectId)?.name || projectId;
    addNotification('info', `Iniciando análisis de ${projectName}...`);

    let eventSource = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 3;
    const reconnectDelay = 2000; // 2 segundos

    const createEventSource = () => {
      try {
        console.log(
          `🔗 Conectando SSE para ${projectId} (intento ${
            reconnectAttempts + 1
          })`
        );

        // Obtener el token del localStorage
        const token = localStorage.getItem('auth_token');
        if (!token) {
          throw new Error('No hay token de autenticación disponible');
        }

        eventSource = new EventSource(
          `${API_BASE}/api/projects/${projectId}/analyze-progress?token=${encodeURIComponent(
            token
          )}`
        );

        eventSource.onopen = () => {
          console.log(`✅ Conexión SSE establecida para ${projectId}`);
          reconnectAttempts = 0; // Resetear contador de intentos
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log(`📨 Evento SSE recibido: ${data.type}`);

            if (data.type === 'start') {
              setProgressData({
                stage: 'fetching',
                progress: 0,
                message: data.message,
              });
            } else if (data.type === 'fetching_conversations') {
              setProgressData({
                stage: 'fetching',
                progress: data.progress,
                message: data.message,
              });
            } else if (data.type === 'fetching_progress') {
              setProgressData({
                stage: 'fetching',
                progress: data.progress,
                message: data.message,
                page: data.page,
                conversationsInPage: data.conversationsInPage,
                totalConversations: data.totalConversations,
              });
            } else if (data.type === 'conversations_fetched') {
              setProgressData({
                stage: 'fetching',
                progress: data.progress,
                message: data.message,
                totalConversations: data.totalConversations,
              });
            } else if (data.type === 'processing_conversations') {
              setProgressData({
                stage: 'processing',
                progress: data.progress,
                message: data.message,
              });
            } else if (data.type === 'processing_progress') {
              setProgressData({
                stage: 'processing',
                progress: data.progress,
                message: data.message,
                processed: data.processed,
                total: data.total,
              });
            } else if (data.type === 'processing_complete') {
              setProgressData({
                stage: 'processing',
                progress: data.progress,
                message: data.message,
              });
            } else if (data.type === 'heartbeat') {
              console.log(`💓 Heartbeat recibido para ${projectId}`);
              // No hacer nada, solo mantener la conexión activa
            } else if (data.type === 'complete') {
              console.log(`✅ Análisis completado para ${projectId}`);
              setAnalysisData(data.data);
              setSelectedProject(projectId);
              setLoading(false);
              setAnalyzingProject(null);
              setProgressData(null);
              addNotification(
                'success',
                `Análisis de ${projectName} completado exitosamente`
              );
              if (eventSource) {
                eventSource.close();
                eventSource = null;
              }
            } else if (data.type === 'error') {
              console.error(`❌ Error en análisis ${projectId}:`, data.error);
              setError(data.error);
              setLoading(false);
              setAnalyzingProject(null);
              setProgressData(null);
              addNotification(
                'error',
                `Error en análisis de ${projectName}: ${data.error}`
              );
              if (eventSource) {
                eventSource.close();
                eventSource = null;
              }
            } else if (data.type === 'end') {
              console.log(`🏁 Stream finalizado para ${projectId}`);
              if (eventSource) {
                eventSource.close();
                eventSource = null;
              }
            }
          } catch (parseError) {
            console.error('Error parseando evento SSE:', parseError);
          }
        };

        eventSource.onerror = (error) => {
          console.error(`❌ Error SSE para ${projectId}:`, error);

          // Solo intentar reconectar si no hemos alcanzado el máximo de intentos
          if (reconnectAttempts < maxReconnectAttempts) {
            reconnectAttempts++;
            console.log(
              `🔄 Intentando reconectar (${reconnectAttempts}/${maxReconnectAttempts})...`
            );

            if (eventSource) {
              eventSource.close();
              eventSource = null;
            }

            // Esperar antes de reconectar
            setTimeout(() => {
              if (analyzingProject === projectId) {
                // Solo reconectar si seguimos analizando este proyecto
                createEventSource();
              }
            }, reconnectDelay);
          } else {
            console.error(
              `❌ Máximo de intentos de reconexión alcanzado para ${projectId}`
            );
            setError(
              'Error de conexión durante el análisis. Se perdieron demasiadas conexiones.'
            );
            setLoading(false);
            setAnalyzingProject(null);
            setProgressData(null);
            addNotification(
              'error',
              `Error de conexión en análisis de ${projectName}. Intente nuevamente.`
            );
            if (eventSource) {
              eventSource.close();
              eventSource = null;
            }
          }
        };

        // Timeout de seguridad para evitar conexiones infinitas
        setTimeout(() => {
          if (eventSource && analyzingProject === projectId) {
            console.log(`⏰ Timeout de seguridad para ${projectId}`);
            setError(
              'Timeout en la conexión. El análisis está tardando más de lo esperado.'
            );
            setLoading(false);
            setAnalyzingProject(null);
            setProgressData(null);
            eventSource.close();
            eventSource = null;
          }
        }, 10 * 60 * 1000); // 10 minutos
      } catch (err) {
        console.error('Error creando EventSource:', err);
        setError('Error de conexión durante el análisis');
        setLoading(false);
        setAnalyzingProject(null);
        setProgressData(null);
      }
    };

    // Iniciar la conexión
    createEventSource();
  };

  const resetView = () => {
    setSelectedProject(null);
    setAnalysisData(null);
    setError(null);
    setProgressData(null);
  };

  const prepareChartData = () => {
    if (!analysisData) return [];

    return [
      {
        name: 'Total Conversaciones',
        value: parseInt(analysisData.totalConversaciones),
      },
      { name: 'Pidieron Operador', value: analysisData.pidieronOperador },
      { name: 'Solo Un Mensaje', value: analysisData.soloUnMensaje },
      { name: 'Abrieron Info', value: analysisData.abrieronInfo },
    ];
  };

  const preparePieData = () => {
    if (!analysisData) return [];

    const total = parseInt(analysisData.totalConversaciones);
    return [
      { name: 'Operador Solicitado', value: analysisData.pidieronOperador },
      { name: 'Info Abierta', value: analysisData.abrieronInfo },
      { name: 'Solo Un Mensaje', value: analysisData.soloUnMensaje },
      {
        name: 'Otros',
        value:
          total -
          analysisData.pidieronOperador -
          analysisData.abrieronInfo -
          analysisData.soloUnMensaje,
      },
    ].filter((item) => item.value > 0);
  };

  const getFilteredSlowConversations = () => {
    if (!analysisData?.conversacionesLentas) return [];
    return analysisData.conversacionesLentas.filter(
      (conv) => conv.horas >= timeFilter.min && conv.horas <= timeFilter.max
    );
  };

  const getPaginatedSlowConversations = () => {
    const filtered = getFilteredSlowConversations();
    const itemsPerPage = 20;
    const startIndex = (slowConversationsPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return {
      conversations: filtered.slice(startIndex, endIndex),
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / itemsPerPage),
      currentPage: slowConversationsPage,
    };
  };

  const handleTimeFilterChange = (field, value) => {
    setTimeFilter((prev) => ({ ...prev, [field]: parseFloat(value) || 0 }));
    setSlowConversationsPage(1); // Resetear a la primera página cuando cambie el filtro
  };

  if (loading) {
    return (
      <div className="app" data-theme={theme}>
        <header className="app-header">
          <div className="header-content">
            <h1>📊 Chatwoot Analytics Dashboard</h1>
            <div className="header-status">
              <ConnectionStatus status={connectionStatus} />
            </div>
          </div>
        </header>
        <main className="app-main">
          {/* Notificaciones */}
          <div className="notifications-container">
            {notifications.map((notification) => (
              <NotificationBanner
                key={notification.id}
                type={notification.type}
                message={notification.message}
                action={notification.action}
                onDismiss={() => removeNotification(notification.id)}
              />
            ))}
          </div>

          {progressData ? (
            <ProgressLoader {...progressData} />
          ) : (
            <LoadingSpinner />
          )}
        </main>
      </div>
    );
  }

  if (selectedProject && analysisData) {
    const project = projects.find((p) => p.id === selectedProject);

    return (
      <div className="app" data-theme={theme}>
        {/* Notificaciones */}
        <div className="notifications-container">
          {notifications.map((notification) => (
            <NotificationBanner
              key={notification.id}
              type={notification.type}
              message={notification.message}
              action={notification.action}
              onDismiss={() => removeNotification(notification.id)}
            />
          ))}
        </div>

        <header className="app-header">
          <div className="header-content">
            <div className="header-title">
              <h1>📊 {project?.name || 'Proyecto'}</h1>
              <div className="header-status">
                <ConnectionStatus status={connectionStatus} />
              </div>
            </div>
            <div className="header-actions">
              <button className="btn btn--secondary" onClick={resetView}>
                <ChevronLeft size={16} />
                Volver a Proyectos
              </button>
              <button
                className={`btn btn--ghost ${autoRefresh ? 'btn--active' : ''}`}
                onClick={() => setAutoRefresh(!autoRefresh)}
                title={
                  autoRefresh
                    ? 'Desactivar auto-actualización'
                    : 'Activar auto-actualización'
                }
              >
                <RefreshCw size={16} className={autoRefresh ? 'spin' : ''} />
              </button>
              <div className="theme-selector">
                <select
                  value={theme}
                  onChange={(e) => changeTheme(e.target.value)}
                >
                  {Object.entries(THEMES).map(([key, themeData]) => (
                    <option key={key} value={key}>
                      {themeData.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </header>

        <main className="app-main">
          <div className="analysis-container">
            <div className="analysis-header">
              <h2>Resultados del Análisis</h2>
              <div className="analysis-meta">
                <span>
                  📅{' '}
                  {new Date(analysisData.metadata?.analyzedAt).toLocaleString()}
                </span>
                <span>⏱️ {analysisData.tiempoEjecucion}</span>
              </div>
            </div>

            <div className="metrics-grid">
              <MetricCard
                title="Total Conversaciones"
                value={analysisData.totalConversaciones}
                icon={MessageSquare}
                color="blue"
              />
              <MetricCard
                title="Pidieron Operador"
                value={analysisData.pidieronOperador}
                icon={Users}
                color="green"
              />
              <MetricCard
                title="Solo Un Mensaje"
                value={analysisData.soloUnMensaje}
                icon={AlertTriangle}
                color="orange"
              />
              <MetricCard
                title="Abrieron Información"
                value={analysisData.abrieronInfo}
                icon={TrendingUp}
                color="purple"
              />
              <MetricCard
                title="Intercambio Promedio"
                value={`${analysisData.intercambioPromedio} msgs`}
                icon={Activity}
                color="indigo"
              />
              <MetricCard
                title="Tiempo Promedio Respuesta"
                value={analysisData.tiempoPromedioRespuesta}
                icon={Clock}
                color="teal"
              />
              <MetricCard
                title="Conversaciones Completadas"
                value={analysisData.totalConversacionesCompletadas || 0}
                icon={CheckCircle}
                color="green"
              />
            </div>

            <div className="charts-container">
              <ChartContainer
                title="📊 Distribución de Conversaciones"
                subtitle="Análisis de volumen por categoría"
                isExpanded={expandedCharts.has('bar-chart')}
                onExpand={() => toggleChartExpansion('bar-chart')}
                onRefresh={() => analyzeProject(selectedProject)}
              >
                <ResponsiveContainer
                  width="100%"
                  height={expandedCharts.has('bar-chart') ? 500 : 300}
                >
                  <ComposedChart data={prepareChartData()}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    />
                    <Bar dataKey="value" fill="#667eea" radius={[4, 4, 0, 0]}>
                      {prepareChartData().map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Bar>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#764ba2"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartContainer>

              <ChartContainer
                title="🥧 Análisis por Categorías"
                subtitle="Distribución porcentual de tipos"
                isExpanded={expandedCharts.has('pie-chart')}
                onExpand={() => toggleChartExpansion('pie-chart')}
                options={
                  <div className="chart-options">
                    <button className="btn btn--small btn--ghost">
                      <FileText size={14} />
                      Exportar
                    </button>
                  </div>
                }
              >
                <ResponsiveContainer
                  width="100%"
                  height={expandedCharts.has('pie-chart') ? 500 : 300}
                >
                  <PieChart>
                    <Pie
                      data={preparePieData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={expandedCharts.has('pie-chart') ? 180 : 100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {preparePieData().map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: 'none',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>

              {/* Nuevo gráfico de tendencias */}
              <ChartContainer
                title="📈 Tendencias en el Tiempo"
                subtitle="Evolución de métricas principales"
                isExpanded={expandedCharts.has('trend-chart')}
                onExpand={() => toggleChartExpansion('trend-chart')}
              >
                <ResponsiveContainer
                  width="100%"
                  height={expandedCharts.has('trend-chart') ? 400 : 250}
                >
                  <AreaChart
                    data={[
                      {
                        name: 'Sem 1',
                        conversaciones: 120,
                        operador: 30,
                        info: 45,
                      },
                      {
                        name: 'Sem 2',
                        conversaciones: 140,
                        operador: 35,
                        info: 50,
                      },
                      {
                        name: 'Sem 3',
                        conversaciones: 160,
                        operador: 40,
                        info: 55,
                      },
                      {
                        name: 'Actual',
                        conversaciones: parseInt(
                          analysisData.totalConversaciones
                        ),
                        operador: analysisData.pidieronOperador,
                        info: analysisData.abrieronInfo,
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="conversaciones"
                      stackId="1"
                      stroke="#667eea"
                      fill="#667eea"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="operador"
                      stackId="2"
                      stroke="#48bb78"
                      fill="#48bb78"
                      fillOpacity={0.6}
                    />
                    <Area
                      type="monotone"
                      dataKey="info"
                      stackId="3"
                      stroke="#ed8936"
                      fill="#ed8936"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>

              {/* Gráfico radar de rendimiento */}
              <ChartContainer
                title="🎯 Radar de Rendimiento"
                subtitle="Vista integral de métricas clave"
                isExpanded={expandedCharts.has('radar-chart')}
                onExpand={() => toggleChartExpansion('radar-chart')}
              >
                <ResponsiveContainer
                  width="100%"
                  height={expandedCharts.has('radar-chart') ? 400 : 250}
                >
                  <RadarChart
                    data={[
                      {
                        subject: 'Volumen',
                        A: Math.min(
                          parseInt(analysisData.totalConversaciones) / 10,
                          100
                        ),
                      },
                      {
                        subject: 'Escalación',
                        A: Math.min(
                          (analysisData.pidieronOperador /
                            parseInt(analysisData.totalConversaciones)) *
                            100,
                          100
                        ),
                      },
                      {
                        subject: 'Información',
                        A: Math.min(
                          (analysisData.abrieronInfo /
                            parseInt(analysisData.totalConversaciones)) *
                            100,
                          100
                        ),
                      },
                      {
                        subject: 'Completitud',
                        A: Math.min(
                          ((parseInt(analysisData.totalConversaciones) -
                            analysisData.soloUnMensaje) /
                            parseInt(analysisData.totalConversaciones)) *
                            100,
                          100
                        ),
                      },
                      { subject: 'Eficiencia', A: 75 }, // Métrica calculada
                    ]}
                  >
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar
                      name="Rendimiento"
                      dataKey="A"
                      stroke="#667eea"
                      fill="#667eea"
                      fillOpacity={0.6}
                    />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>

            {analysisData.conversacionesLentas?.length > 0 && (
              <div className="slow-conversations">
                <div className="slow-conversations-header">
                  <h3>⚠️ Conversaciones con Atención Demorada</h3>
                  <div className="time-filter">
                    <label>
                      Filtro de tiempo:
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={timeFilter.min}
                        onChange={(e) =>
                          handleTimeFilterChange('min', e.target.value)
                        }
                        placeholder="Min horas"
                        style={{ width: '80px', margin: '0 8px' }}
                      />
                      -
                      <input
                        type="number"
                        min="0"
                        max="200"
                        value={timeFilter.max}
                        onChange={(e) =>
                          handleTimeFilterChange('max', e.target.value)
                        }
                        placeholder="Max horas"
                        style={{ width: '80px', margin: '0 8px' }}
                      />
                      horas
                    </label>
                  </div>
                </div>
                <div className="slow-conversations-list">
                  {getPaginatedSlowConversations().conversations.map(
                    (conv, index) => (
                      <div key={conv.id} className="slow-conversation-item">
                        <a
                          href={`${analysisData.chatwootBaseUrl}/accounts/${analysisData.accountId}/conversations/${conv.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="conversation-id-link"
                        >
                          #{conv.id}
                        </a>
                        <span className="conversation-time">
                          {conv.horas.toFixed(2)}h
                        </span>
                      </div>
                    )
                  )}
                </div>
                {getPaginatedSlowConversations().totalPages > 1 && (
                  <div className="pagination-controls">
                    <button
                      className="btn btn--secondary"
                      onClick={() =>
                        setSlowConversationsPage((prev) =>
                          Math.max(1, prev - 1)
                        )
                      }
                      disabled={
                        getPaginatedSlowConversations().currentPage === 1
                      }
                    >
                      ← Anterior
                    </button>
                    <span className="pagination-info">
                      Página {getPaginatedSlowConversations().currentPage} de{' '}
                      {getPaginatedSlowConversations().totalPages} (
                      {getPaginatedSlowConversations().total} conversaciones)
                    </span>
                    <button
                      className="btn btn--secondary"
                      onClick={() =>
                        setSlowConversationsPage((prev) =>
                          Math.min(
                            getPaginatedSlowConversations().totalPages,
                            prev + 1
                          )
                        )
                      }
                      disabled={
                        getPaginatedSlowConversations().currentPage ===
                        getPaginatedSlowConversations().totalPages
                      }
                    >
                      Siguiente →
                    </button>
                  </div>
                )}
                {getPaginatedSlowConversations().total === 0 && (
                  <p className="no-conversations">
                    No hay conversaciones en el rango de tiempo seleccionado.
                  </p>
                )}
              </div>
            )}

            {analysisData.palabrasClave &&
              Object.keys(analysisData.palabrasClave).length > 0 && (
                <div className="keywords-analysis">
                  <h3>🔍 Análisis de Palabras Clave</h3>
                  <div className="keywords-table">
                    <div className="keywords-header">
                      <span>Palabra Clave</span>
                      <span>Conversaciones</span>
                      <span>Ocurrencias Totales</span>
                      <span>Promedio por Conversación</span>
                    </div>
                    {Object.entries(analysisData.palabrasClave)
                      .sort((a, b) => b[1].conversaciones - a[1].conversaciones)
                      .map(([palabra, datos]) => (
                        <div key={palabra} className="keywords-row">
                          <span className="keyword-name">{palabra}</span>
                          <span className="keyword-conversations">
                            {datos.conversaciones}
                          </span>
                          <span className="keyword-occurrences">
                            {datos.ocurrencias}
                          </span>
                          <span className="keyword-average">
                            {(datos.ocurrencias / datos.conversaciones).toFixed(
                              2
                            )}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

            {analysisData.conversacionesCompletadas?.length > 0 && (
              <div className="completed-conversations-analysis">
                <h3>🎯 Conversaciones Completadas con Múltiples Ciclos</h3>
                <p className="analysis-description">
                  Conversaciones que tuvieron al menos 2 inicios y 2 cierres,
                  indicando un flujo completo de interacción.
                </p>
                <div className="completed-conversations-table">
                  <div className="completed-conversations-header">
                    <span>ID Conversación</span>
                    <span>Inicios</span>
                    <span>Cierres</span>
                    <span>Estado</span>
                  </div>
                  {analysisData.conversacionesCompletadas
                    .sort(
                      (a, b) => b.inicios + b.cierres - (a.inicios + a.cierres)
                    )
                    .slice(0, 15)
                    .map((conv) => (
                      <div key={conv.id} className="completed-conversation-row">
                        <a
                          href={`${analysisData.chatwootBaseUrl}/accounts/${analysisData.accountId}/conversations/${conv.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="conversation-id-link"
                        >
                          #{conv.id}
                        </a>
                        <span className="inicios-count">{conv.inicios}</span>
                        <span className="cierres-count">{conv.cierres}</span>
                        <span className="completion-status">✅ Completada</span>
                      </div>
                    ))}
                </div>
                {analysisData.conversacionesCompletadas.length > 15 && (
                  <p className="more-conversations">
                    Y {analysisData.conversacionesCompletadas.length - 15}{' '}
                    conversaciones completadas más...
                  </p>
                )}
              </div>
            )}

            <div className="additional-metrics">
              <div className="metric-row">
                <strong>Tiempo Promedio Atención Humana:</strong>
                <span>{analysisData.tiempoPromedioAtencionHumana}</span>
              </div>
              {analysisData.errores > 0 && (
                <div className="metric-row error">
                  <strong>Errores durante el análisis:</strong>
                  <span>{analysisData.errores}</span>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="app" data-theme={theme}>
      <Header />
      {/* Notificaciones */}
      <div className="notifications-container">
        {notifications.map((notification) => (
          <NotificationBanner
            key={notification.id}
            type={notification.type}
            message={notification.message}
            action={notification.action}
            onDismiss={() => removeNotification(notification.id)}
          />
        ))}
      </div>

      {/* Header personalizado con autenticación ya agregado arriba */}
      <div className="app-toolbar">
        <div className="toolbar-content">
          <div className="header-title">
            <h1>📊 Chatwoot Analytics Dashboard</h1>
            <p>Panel avanzado de análisis de conversaciones</p>
          </div>
          <div className="header-status">
            <ConnectionStatus status={connectionStatus} />
          </div>
        </div>

        <div className="header-toolbar">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Buscar proyectos..."
            filters={[
              { label: 'Favoritos', value: 'favorites' },
              { label: 'Recientes', value: 'recent' },
            ]}
            onFilterSelect={(filter) => {
              if (filter.value === 'favorites') {
                // Implementar filtro de favoritos
              }
            }}
          />

          <div className="header-actions">
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={resetFilters}
              isOpen={filtersOpen}
              onToggle={() => setFiltersOpen(!filtersOpen)}
            />

            <button
              className={`btn btn--ghost ${
                viewMode === 'grid' ? 'btn--active' : ''
              }`}
              onClick={() => setViewMode('grid')}
              title="Vista en cuadrícula"
            >
              <BarChart3 size={16} />
            </button>

            <button
              className={`btn btn--ghost ${
                viewMode === 'list' ? 'btn--active' : ''
              }`}
              onClick={() => setViewMode('list')}
              title="Vista en lista"
            >
              <FileText size={16} />
            </button>

            <button
              className="btn btn--secondary"
              onClick={() => setShowCacheManager(!showCacheManager)}
            >
              {showCacheManager ? (
                <>
                  <BarChart3 size={16} />
                  Ver Proyectos
                </>
              ) : (
                <>
                  <Database size={16} />
                  Gestor de Caché
                </>
              )}
            </button>

            <button
              className="btn btn--secondary"
              onClick={() => setShowProjectManager(!showProjectManager)}
            >
              {showProjectManager ? (
                <>
                  <BarChart3 size={16} />
                  Ver Proyectos
                </>
              ) : (
                <>
                  <Settings size={16} />
                  Gestor de Proyectos
                </>
              )}
            </button>

            <button
              className={`btn btn--ghost ${autoRefresh ? 'btn--active' : ''}`}
              onClick={() => setAutoRefresh(!autoRefresh)}
              title={
                autoRefresh
                  ? 'Desactivar auto-actualización'
                  : 'Activar auto-actualización'
              }
            >
              <RefreshCw size={16} className={autoRefresh ? 'spin' : ''} />
            </button>

            <div className="theme-selector">
              <select
                value={theme}
                onChange={(e) => changeTheme(e.target.value)}
              >
                {Object.entries(THEMES).map(([key, themeData]) => (
                  <option key={key} value={key}>
                    {themeData.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <main className="app-main">
        {error && (
          <div className="error-message">
            <AlertTriangle size={20} />
            <span>{error}</span>
          </div>
        )}

        {showCacheManager ? (
          <CacheManager
            cacheInfo={cacheInfo}
            projectsCacheInfo={projectsCacheInfo}
            projects={projects}
            onClearAll={clearAllCache}
            onClearExpired={clearExpiredCache}
            onDeleteSpecific={deleteSpecificCache}
            onRefresh={async () => {
              await fetchCacheInfo();
              await fetchProjectsCacheInfo();
              addNotification('info', 'Información de caché actualizada');
            }}
            loading={cacheLoading}
          />
        ) : showProjectManager ? (
          <ProjectManager
            projects={projects}
            onAddProject={addProject}
            onEditProject={editProject}
            onDeleteProject={deleteProject}
            onRefresh={fetchProjects}
            loading={projectManagerLoading}
          />
        ) : (
          <>
            {filteredProjects.length > 0 ? (
              <div
                className={`projects-container projects-container--${viewMode}`}
              >
                <div className="projects-header">
                  <div className="projects-stats">
                    <span>{filteredProjects.length} proyectos</span>
                    {favoriteProjects.size > 0 && (
                      <span>• {favoriteProjects.size} favoritos</span>
                    )}
                    {searchQuery && <span>• filtrado por "{searchQuery}"</span>}
                  </div>
                </div>

                <div className={`projects-${viewMode}`}>
                  {filteredProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onAnalyze={analyzeProject}
                      isLoading={analyzingProject === project.id}
                      onFavorite={toggleFavorite}
                      isFavorite={favoriteProjects.has(project.id)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-state">
                {searchQuery ? (
                  <>
                    <Search size={48} />
                    <h3>No se encontraron proyectos</h3>
                    <p>No hay proyectos que coincidan con "{searchQuery}"</p>
                    <button
                      className="btn btn--secondary"
                      onClick={() => setSearchQuery('')}
                    >
                      <RotateCcw size={16} />
                      Limpiar búsqueda
                    </button>
                  </>
                ) : projects.length === 0 ? (
                  <>
                    <Activity size={48} />
                    <h3>No hay proyectos configurados</h3>
                    <p>Agrega analizadores en la carpeta server/analyzers/</p>
                    <button
                      className="btn btn--primary"
                      onClick={fetchProjects}
                    >
                      <RefreshCw size={16} />
                      Recargar proyectos
                    </button>
                  </>
                ) : (
                  <>
                    <Filter size={48} />
                    <h3>Sin resultados</h3>
                    <p>Ajusta los filtros para ver más proyectos</p>
                    <button
                      className="btn btn--secondary"
                      onClick={resetFilters}
                    >
                      <RotateCcw size={16} />
                      Resetear filtros
                    </button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

// Componente principal con autenticación
function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <DashboardApp />
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;
